import React, { useState, useRef, useMemo, useCallback } from 'react';
import { Group, Rect, Text, Circle, Image } from 'react-konva';
import useImage from 'use-image';
import { useSelector, useDispatch } from 'react-redux';

import CanvasUIElementRenderer from './CanvasUIElementRenderer';
import ElementContextMenu from './ElementContextMenu';
import { elementClipboard } from './utils/clipboard';
import {
    deleteElementWithUndo,
    copyElementWithUndo,
} from './utils/undoRedo/actionHelpers';
import { getDeviceDimensions, getDeviceById } from './deviceDefinitions';
import { RootState } from './utils/experienceStore';
import useCustomDisplays from './hooks/useCustomDisplays';
import Konva from 'konva';

interface ScreenCanvasNodeProps {
    screen: ScreenDefinition;
    position: { x: number; y: number };
    isSelected: boolean;
    selectedElementId?: string;
    onSelect: () => void;
    onZoomToScreen: () => void;
    onElementSelect: (elementId: string) => void;
    onDragEnd: (newPosition: { x: number; y: number }) => void;
    isDraggable: boolean;
    onElementDrop?: (
        elementType: UIElementType,
        gridPosition: { h: string; v: string },
        groupId?: string,
    ) => void;
    isDragFromPalette?: boolean;
    draggedElementType?: UIElementType | null;
    dragHoverPosition?: { h: string; v: string };
    onRightClick?: (element: UIElement, screenId: string, event: any) => void;
    onElementDragStart?: (
        element: UIElement,
        screenId: string,
        event: any,
    ) => void;
}

const CANVAS_SCALE = 0.25; // Scale factor for canvas preview (25% of actual size)

const ScreenCanvasNode = React.memo(function ScreenCanvasNode({
    screen,
    position,
    isSelected,
    selectedElementId,
    onSelect,
    onZoomToScreen,
    onElementSelect,
    onDragEnd,
    isDraggable,
    onElementDrop,
    isDragFromPalette = false,
    draggedElementType = null,
    dragHoverPosition,
    onRightClick,
    onElementDragStart,
}: ScreenCanvasNodeProps) {
    const groupRef = useRef<Konva.Group>(null);
    const [isDragging, setIsDragging] = useState(false);

    // Get device configuration from Redux
    const { experienceDevices, activeTab, gridColor } = useSelector(
        (state: RootState) => state.canvas,
    );

    // Get custom displays from localStorage
    const { customDisplays } = useCustomDisplays();

    // Calculate device-aware screen dimensions for current experience
    const currentDevice =
        experienceDevices[activeTab] || experienceDevices.main;
    const device = getDeviceById(currentDevice.deviceId, customDisplays);
    const deviceDimensions = getDeviceDimensions(
        device,
        currentDevice.orientation,
    );
    const SCREEN_WIDTH = deviceDimensions.width * CANVAS_SCALE;
    const SCREEN_HEIGHT = deviceDimensions.height * CANVAS_SCALE;

    // Combine canvas preview scale with device scale factor for accurate image rendering
    const scale = CANVAS_SCALE * device.scale;

    // Helper function for device-aware scaling of UI elements (borders, radii, etc.)
    // This scales small UI elements like borders and radii appropriately for the device
    const scaleUIElement = useCallback(
        (value: number) => {
            // Use a smaller scaling factor for UI elements to keep them visible but proportional
            return Math.max(0.5, value * scale * 0.5);
        },
        [scale],
    );

    // Memoize display properties to help with re-rendering
    useMemo(() => {
        console.log(
            `ScreenCanvasNode ${screen.id} display properties changed:`,
            screen.displayProperties,
        );
        return JSON.stringify(screen.displayProperties);
    }, [screen.displayProperties, screen.id]);

    const [isDragOverScreen, setIsDragOverScreen] = useState(false);

    const handleDragStart = () => {
        setIsDragging(true);
    };

    const handleDragEnd = (e: any) => {
        setIsDragging(false);
        const node = e.target;
        onDragEnd({
            x: node.x(),
            y: node.y(),
        });
    };

    // Old click handler - now using handleScreenClick
    // const handleClick = (e: any) => {
    //     e.cancelBubble = true;
    //     onSelect();
    // };

    const handleDoubleClick = (e: any) => {
        e.cancelBubble = true;
        onZoomToScreen();
    };

    // Parse background color from hex string
    const parseColor = (hexColor: string) => {
        if (!hexColor || !hexColor.startsWith('#')) return '#FFFFFF';
        return hexColor;
    };

    const backgroundColor = parseColor(
        screen.displayProperties.backgroundColor,
    );

    // Load background image if available
    const backgroundImageUrl = screen.displayProperties.backgroundMediaUrl;
    const [backgroundImage, backgroundStatus] = useImage(
        backgroundImageUrl || '',
    );

    // Debug logging for background image
    console.log('Screen background:', {
        screenId: screen.id,
        backgroundImageUrl,
        backgroundStatus,
        hasBackgroundImage: !!backgroundImage,
    });

    // Render 9-grid layout guides
    const renderGridLayout = () => {
        if (!isSelected) return null;

        // Apply padding settings to the grid layout
        const horizontalPadding =
            (screen.displayProperties.horizontalPadding || 0) * CANVAS_SCALE;
        const verticalPadding =
            (screen.displayProperties.verticalPadding || 0) * CANVAS_SCALE;

        const previewX = 8 + horizontalPadding;
        const previewY = 8 + verticalPadding;
        const previewWidth = SCREEN_WIDTH - 16 - horizontalPadding * 2;
        const previewHeight = SCREEN_HEIGHT - 16 - verticalPadding * 2;
        const cellWidth = previewWidth / 3;
        const cellHeight = previewHeight / 3;

        const gridLines: React.ReactElement[] = [];

        // Vertical lines
        for (let i = 1; i < 3; i++) {
            gridLines.push(
                <Rect
                    key={`v-${i}`}
                    x={previewX + cellWidth * i}
                    y={previewY}
                    width={1}
                    height={previewHeight}
                    fill={gridColor}
                    opacity={0.3}
                />,
            );
        }

        // Horizontal lines
        for (let i = 1; i < 3; i++) {
            gridLines.push(
                <Rect
                    key={`h-${i}`}
                    x={previewX}
                    y={previewY + cellHeight * i}
                    width={previewWidth}
                    height={1}
                    fill={gridColor}
                    opacity={0.3}
                />,
            );
        }

        return gridLines;
    };

    // Render children within a group container based on group layout settings
    const renderGroupChildren = (
        children: UIElement[],
        groupElement: UIElement,
        groupWidth: number,
        groupHeight: number,
    ): React.ReactElement[] => {
        if (children.length === 0) return [];

        const groupMetadata = groupElement.metadata as any;
        const direction = groupMetadata.direction || 'column';
        const spacing = (groupMetadata.spacing || 8) * scale;
        const alignment = groupMetadata.alignment || 'center';

        // Calculate available space for children (with padding)
        const padding = 8 * scale;
        const availableWidth = groupWidth - padding * 2;
        const availableHeight = groupHeight - padding * 2;

        const childElements: React.ReactElement[] = [];
        let currentX = padding;
        let currentY = padding;

        children.forEach((child, index) => {
            // Calculate child size (simplified for preview)
            const childWidth = 40 * scale;
            const childHeight = 20 * scale;

            // Position based on direction
            if (direction === 'row') {
                // Horizontal layout
                if (index > 0) currentX += spacing;

                // Vertical alignment within row
                let childY = currentY;
                switch (alignment) {
                    case 'leading':
                        childY = padding;
                        break;
                    case 'center':
                        childY = padding + (availableHeight - childHeight) / 2;
                        break;
                    case 'trailing':
                        childY = padding + availableHeight - childHeight;
                        break;
                }

                childElements.push(
                    <Group key={child.id} x={currentX} y={childY}>
                        <CanvasUIElementRenderer
                            element={child}
                            isSelected={
                                selectedElementId === child.id && isSelected
                            }
                            onSelect={() => {
                                if (!isSelected) {
                                    onSelect();
                                }
                                onElementSelect(child.id);
                            }}
                            scale={scale}
                            screenId={screen.id}
                            isStacked={false}
                            stackNumber={1}
                            displayProperties={{
                                foregroundColor:
                                    screen.displayProperties.foregroundColor,
                                buttonColor:
                                    screen.displayProperties.buttonColor,
                                buttonTextColor:
                                    screen.displayProperties.buttonTextColor,
                                backgroundColor:
                                    screen.displayProperties.backgroundColor,
                            }}
                            screenDimensions={{
                                width: SCREEN_WIDTH,
                                height: SCREEN_HEIGHT,
                            }}
                            onRightClick={onRightClick}
                            onElementDragStart={onElementDragStart}
                        />
                    </Group>,
                );

                currentX += childWidth;
            } else {
                // Vertical layout (column)
                if (index > 0) currentY += spacing;

                // Horizontal alignment within column
                let childX = currentX;
                switch (alignment) {
                    case 'leading':
                        childX = padding;
                        break;
                    case 'center':
                        childX = padding + (availableWidth - childWidth) / 2;
                        break;
                    case 'trailing':
                        childX = padding + availableWidth - childWidth;
                        break;
                }

                childElements.push(
                    <Group key={child.id} x={childX} y={currentY}>
                        <CanvasUIElementRenderer
                            element={child}
                            isSelected={
                                selectedElementId === child.id && isSelected
                            }
                            onSelect={() => {
                                if (!isSelected) {
                                    onSelect();
                                }
                                onElementSelect(child.id);
                            }}
                            scale={scale}
                            screenId={screen.id}
                            isStacked={false}
                            stackNumber={1}
                            displayProperties={{
                                foregroundColor:
                                    screen.displayProperties.foregroundColor,
                                buttonColor:
                                    screen.displayProperties.buttonColor,
                                buttonTextColor:
                                    screen.displayProperties.buttonTextColor,
                                backgroundColor:
                                    screen.displayProperties.backgroundColor,
                            }}
                            screenDimensions={{
                                width: SCREEN_WIDTH,
                                height: SCREEN_HEIGHT,
                            }}
                            onRightClick={onRightClick}
                            onElementDragStart={onElementDragStart}
                        />
                    </Group>,
                );

                currentY += childHeight;
            }
        });

        return childElements;
    };

    // Render UI elements with full visual representations
    const renderElements = () => {
        // Apply padding settings to the content area
        const horizontalPadding =
            (screen.displayProperties.horizontalPadding || 0) * CANVAS_SCALE;
        const verticalPadding =
            (screen.displayProperties.verticalPadding || 0) * CANVAS_SCALE;

        const previewX = 8 + horizontalPadding;
        const previewY = 8 + verticalPadding;
        const previewWidth = SCREEN_WIDTH - 16 - horizontalPadding * 2;
        const previewHeight = SCREEN_HEIGHT - 16 - verticalPadding * 2;
        const cellWidth = previewWidth / 3;
        const cellHeight = previewHeight / 3;

        const elements: React.ReactElement[] = [];

        // Separate group elements from regular elements
        // Ensure elements array exists, default to empty array if undefined
        const screenElements = screen.elements || [];
        const groupElements = screenElements.filter(
            (el) => el.type === 'group',
        );
        const regularElements = screenElements.filter(
            (el) => el.type !== 'group' && !el.groupLocation,
        );
        const groupedElements = screenElements.filter(
            (el) => el.type !== 'group' && el.groupLocation,
        );

        // Group regular elements by layer for proper rendering order
        const elementsByLayer = regularElements.reduce((acc, element) => {
            const layer = element.layer || 0;
            if (!acc[layer]) acc[layer] = [];
            acc[layer].push(element);
            return acc;
        }, {} as { [layer: number]: UIElement[] });

        // Render elements layer by layer (0, 1, 2)
        [0, 1, 2].forEach((layer) => {
            const layerElements = elementsByLayer[layer] || [];

            // Group elements by their grid position for column stacking
            const elementsByPosition = layerElements.reduce((acc, element) => {
                const posKey = `${element.horizontalLocation}-${element.verticalLocation}`;
                if (!acc[posKey]) acc[posKey] = [];
                acc[posKey].push(element);
                return acc;
            }, {} as { [position: string]: UIElement[] });

            // Process each position group
            Object.entries(elementsByPosition).forEach(
                ([posKey, positionElements]) => {
                    positionElements.forEach((element, stackIndex) => {
                        // Map element position to grid coordinates
                        let gridX = 0,
                            gridY = 0;

                        switch (element.horizontalLocation) {
                            case 'leading':
                            case 'left': // Fallback for legacy data
                                gridX = 0;
                                break;
                            case 'center':
                                gridX = 1;
                                break;
                            case 'trailing':
                            case 'right': // Fallback for legacy data
                                gridX = 2;
                                break;
                            default:
                                gridX = 1;
                        }

                        switch (element.verticalLocation) {
                            case 'top':
                                gridY = 0;
                                break;
                            case 'center':
                                gridY = 1;
                                break;
                            case 'bottom':
                                gridY = 2;
                                break;
                            default:
                                gridY = 1;
                        }

                        // Calculate anchor position in grid cell based on location
                        const baseCellX = previewX + gridX * cellWidth;
                        const baseCellY = previewY + gridY * cellHeight;

                        // Center the element within its assigned grid cell
                        // baseCellX and baseCellY already position us in the correct 3x3 grid cell
                        // Now just center within that cell (like CSS flexbox justify-content: center, align-items: center)
                        let anchorX = baseCellX + cellWidth / 2; // Center horizontally in the cell
                        let anchorY = baseCellY + cellHeight / 2; // Center vertically in the cell

                        // Debug logging for positioning
                        console.log(
                            `Element ${element.id} (${element.horizontalLocation}-${element.verticalLocation}):`,
                            {
                                cellDimensions: {
                                    width: cellWidth,
                                    height: cellHeight,
                                },
                                cellPosition: { x: baseCellX, y: baseCellY },
                                finalAnchor: { x: anchorX, y: anchorY },
                                elementType: element.type,
                                gridPosition: { gridX, gridY },
                            },
                        );

                        // Apply element offsets and margins from metadata
                        const elementMetadata = element.metadata as any;
                        const horizontalOffset =
                            (elementMetadata.horizontalOffset || 0) *
                            scale *
                            0.5; // Scale down for canvas
                        const verticalOffset =
                            (elementMetadata.verticalOffset || 0) * scale * 0.5; // Scale down for canvas

                        // Apply margins (additional spacing from grid position)
                        const horizontalMargin =
                            (elementMetadata.horizontalMargin || 0) *
                            scale *
                            0.5; // Scale down for canvas
                        const verticalMargin =
                            (elementMetadata.verticalMargin || 0) * scale * 0.5; // Scale down for canvas

                        anchorX += horizontalOffset + horizontalMargin;
                        anchorY += verticalOffset + verticalMargin;

                        // Apply column stacking offset for elements in same position
                        const stackOffset = stackIndex * 25 * scale; // 25px spacing between stacked elements
                        anchorY += stackOffset;

                        // Add visual indicator for stacked elements
                        const isStacked = positionElements.length > 1;
                        const stackNumber = stackIndex + 1;

                        elements.push(
                            <Group key={element.id} x={anchorX} y={anchorY}>
                                <CanvasUIElementRenderer
                                    element={element}
                                    isSelected={
                                        selectedElementId === element.id &&
                                        isSelected
                                    }
                                    onSelect={() => {
                                        // First select the screen, then the element
                                        if (!isSelected) {
                                            onSelect(); // Select the screen first
                                        }
                                        onElementSelect(element.id);
                                    }}
                                    scale={scale}
                                    screenId={screen.id}
                                    isStacked={isStacked}
                                    stackNumber={stackNumber}
                                    displayProperties={{
                                        foregroundColor:
                                            screen.displayProperties
                                                .foregroundColor,
                                        buttonColor:
                                            screen.displayProperties
                                                .buttonColor,
                                        buttonTextColor:
                                            screen.displayProperties
                                                .buttonTextColor,
                                        backgroundColor:
                                            screen.displayProperties
                                                .backgroundColor,
                                    }}
                                    screenDimensions={{
                                        width: SCREEN_WIDTH,
                                        height: SCREEN_HEIGHT,
                                    }}
                                    onRightClick={onRightClick}
                                    onElementDragStart={onElementDragStart}
                                />
                            </Group>,
                        );
                    });
                },
            );
        });

        // Render group elements with their children
        groupElements.forEach((groupElement) => {
            // Find children of this group
            const groupChildren = groupedElements.filter(
                (el) => el.groupLocation === groupElement.id,
            );

            // Calculate group position using standard grid positioning
            let gridX = 0,
                gridY = 0;
            switch (groupElement.horizontalLocation) {
                case 'leading':
                case 'left':
                    gridX = 0;
                    break;
                case 'center':
                    gridX = 1;
                    break;
                case 'trailing':
                case 'right':
                    gridX = 2;
                    break;
            }

            switch (groupElement.verticalLocation) {
                case 'top':
                    gridY = 0;
                    break;
                case 'center':
                    gridY = 1;
                    break;
                case 'bottom':
                    gridY = 2;
                    break;
            }

            const cellX = previewX + gridX * cellWidth;
            const cellY = previewY + gridY * cellHeight;

            // Group dimensions - make larger if it has children
            const hasChildren = groupChildren.length > 0;
            const groupWidth = hasChildren
                ? Math.max(120, cellWidth * 0.8)
                : 80;
            const groupHeight = hasChildren
                ? Math.max(100, cellHeight * 0.6)
                : 60;

            // Center the group within its cell
            const groupMetadata = groupElement.metadata as any;
            const anchorX =
                cellX +
                (cellWidth - groupWidth) / 2 +
                (groupMetadata.horizontalOffset || 0) * scale;
            const anchorY =
                cellY +
                (cellHeight - groupHeight) / 2 +
                (groupMetadata.verticalOffset || 0) * scale;

            elements.push(
                <Group key={groupElement.id} x={anchorX} y={anchorY}>
                    {/* Group container */}
                    <CanvasUIElementRenderer
                        element={groupElement}
                        isSelected={
                            selectedElementId === groupElement.id && isSelected
                        }
                        onSelect={() => {
                            if (!isSelected) {
                                onSelect();
                            }
                            onElementSelect(groupElement.id);
                        }}
                        scale={scale}
                        screenId={screen.id}
                        isStacked={false}
                        stackNumber={1}
                        displayProperties={{
                            foregroundColor:
                                screen.displayProperties.foregroundColor,
                            buttonColor: screen.displayProperties.buttonColor,
                            buttonTextColor:
                                screen.displayProperties.buttonTextColor,
                            backgroundColor:
                                screen.displayProperties.backgroundColor,
                        }}
                        screenDimensions={{
                            width: SCREEN_WIDTH,
                            height: SCREEN_HEIGHT,
                        }}
                        onRightClick={onRightClick}
                    />

                    {/* Group drop zone when dragging from palette */}
                    {isDragFromPalette && draggedElementType && (
                        <Rect
                            width={groupWidth}
                            height={groupHeight}
                            fill="#3B82F640"
                            stroke="#3B82F6"
                            strokeWidth={2}
                            cornerRadius={scaleUIElement(8)}
                            onMouseEnter={() => {
                                // Set hover state for group drop zone
                            }}
                            onMouseLeave={() => {
                                // Clear hover state
                            }}
                            onClick={() => {
                                if (onElementDrop && draggedElementType) {
                                    onElementDrop(
                                        draggedElementType,
                                        { h: 'center', v: 'center' }, // Default position within group
                                        groupElement.id, // Group ID for containment
                                    );
                                }
                            }}
                            screenDimensions={{
                                width: SCREEN_WIDTH,
                                height: SCREEN_HEIGHT,
                            }}
                            onRightClick={onRightClick}
                            onElementDragStart={onElementDragStart}
                        />
                    )}

                    {/* Render children within the group */}
                    {renderGroupChildren(
                        groupChildren,
                        groupElement,
                        groupWidth,
                        groupHeight,
                    )}
                </Group>,
            );
        });

        return elements;
    };

    // Update drag state based on parent props
    React.useEffect(() => {
        setIsDragOverScreen(isDragFromPalette);
    }, [isDragFromPalette]);

    // Handle screen selection
    const handleScreenClick = (e: any) => {
        // Don't interfere with drag operations
        if (isDragFromPalette) {
            e.cancelBubble = true;
            return;
        }

        // Normal selection behavior
        onSelect();
    };

    // Memoized render drop zone highlights for better performance
    const renderDropZones = useMemo(() => {
        if (!isDragFromPalette) return null;

        const zones = [];
        // Apply padding settings to match grid layout and element positioning
        const horizontalPadding =
            (screen.displayProperties.horizontalPadding || 0) * CANVAS_SCALE;
        const verticalPadding =
            (screen.displayProperties.verticalPadding || 0) * CANVAS_SCALE;

        const previewX = 8 + horizontalPadding;
        const previewY = 8 + verticalPadding;
        const previewWidth = SCREEN_WIDTH - 16 - horizontalPadding * 2;
        const previewHeight = SCREEN_HEIGHT - 16 - verticalPadding * 2;

        // Create 3x3 grid of drop zones
        const positions = ['leading', 'center', 'trailing'];
        const verticals = ['top', 'center', 'bottom'];

        for (let vIndex = 0; vIndex < verticals.length; vIndex++) {
            for (let hIndex = 0; hIndex < positions.length; hIndex++) {
                const h = positions[hIndex];
                const v = verticals[vIndex];

                // Check if this cell is being hovered during drag
                const isHovered =
                    dragHoverPosition?.h === h && dragHoverPosition?.v === v;

                zones.push(
                    <Rect
                        key={`drop-zone-${h}-${v}`}
                        x={previewX + (hIndex * previewWidth) / 3}
                        y={previewY + (vIndex * previewHeight) / 3}
                        width={previewWidth / 3}
                        height={previewHeight / 3}
                        fill={
                            isHovered
                                ? '#3B82F640' // Strong highlight for hover
                                : '#F3F4F610' // Light background for non-hovered
                        }
                        stroke={isHovered ? '#3B82F6' : '#D1D5DB'}
                        strokeWidth={isHovered ? 3 : 1}
                        strokeDasharray={isHovered ? [4, 4] : [6, 6]}
                        opacity={isHovered ? 1 : 0.8}
                    />,
                );

                // Add element type label and plus icon for hovered cell
                if (isHovered && draggedElementType) {
                    const centerX =
                        previewX +
                        (hIndex * previewWidth) / 3 +
                        previewWidth / 6;
                    const centerY =
                        previewY +
                        (vIndex * previewHeight) / 3 +
                        previewHeight / 6;

                    // Plus icon background circle
                    zones.push(
                        <Circle
                            key={`drop-circle-${h}-${v}`}
                            x={centerX}
                            y={centerY - 8}
                            radius={12}
                            fill="#3B82F6"
                            opacity={0.9}
                        />,
                    );

                    // Plus icon
                    zones.push(
                        <Text
                            key={`drop-plus-${h}-${v}`}
                            x={centerX}
                            y={centerY - 8}
                            text="+"
                            fontSize={14}
                            fontFamily="Arial"
                            fill="white"
                            width={24}
                            height={24}
                            align="center"
                            verticalAlign="middle"
                            fontStyle="bold"
                            offsetX={12}
                            offsetY={12}
                        />,
                    );

                    // Element type label below
                    zones.push(
                        <Text
                            key={`drop-label-${h}-${v}`}
                            x={centerX}
                            y={centerY + 8}
                            text={draggedElementType.toUpperCase()}
                            fontSize={7}
                            fontFamily="Arial"
                            fill="#3B82F6"
                            width={previewWidth / 3}
                            align="center"
                            fontStyle="bold"
                            offsetX={previewWidth / 3 / 2}
                        />,
                    );
                }
            }
        }

        return zones;
    }, [
        isDragFromPalette,
        dragHoverPosition,
        draggedElementType,
        SCREEN_WIDTH,
        SCREEN_HEIGHT,
        screen.displayProperties.horizontalPadding,
        screen.displayProperties.verticalPadding,
    ]);

    return (
        <Group
            ref={groupRef}
            x={position.x}
            y={position.y}
            draggable={isDraggable}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onClick={handleScreenClick}
            onDblClick={handleDoubleClick}
        >
            {/* Selection outline */}
            {isSelected && (
                <Rect
                    x={-4}
                    y={-4}
                    width={SCREEN_WIDTH + 8}
                    height={SCREEN_HEIGHT + 8}
                    stroke="#3B82F6"
                    strokeWidth={3}
                    cornerRadius={scaleUIElement(8)}
                    dash={[8, 8]}
                />
            )}

            {/* Main screen container */}
            <Rect
                x={0}
                y={0}
                width={SCREEN_WIDTH}
                height={SCREEN_HEIGHT}
                fill={screen.displayProperties.backgroundColor || '#EEEFEF'}
                stroke="#D1D5DB"
                strokeWidth={scaleUIElement(1)}
                cornerRadius={scaleUIElement(8)}
                shadowColor="#000000"
                shadowOffset={{ x: 2, y: 2 }}
                shadowOpacity={0.1}
                shadowBlur={4}
            />

            {/* Screen preview area */}
            <Rect
                x={8}
                y={8}
                width={SCREEN_WIDTH - 16}
                height={SCREEN_HEIGHT - 16}
                fill={
                    screen.displayProperties.backgroundColor || backgroundColor
                }
                cornerRadius={scaleUIElement(4)}
                stroke="#D1D5DB"
                strokeWidth={scaleUIElement(1)}
            />

            {/* Content area (with padding applied) */}
            {(() => {
                const horizontalPadding =
                    (screen.displayProperties.horizontalPadding || 0) *
                    CANVAS_SCALE;
                const verticalPadding =
                    (screen.displayProperties.verticalPadding || 0) *
                    CANVAS_SCALE;

                // Only show content area if there's padding
                if (horizontalPadding > 0 || verticalPadding > 0) {
                    return (
                        <Rect
                            x={8 + horizontalPadding}
                            y={8 + verticalPadding}
                            width={SCREEN_WIDTH - 16 - horizontalPadding * 2}
                            height={SCREEN_HEIGHT - 16 - verticalPadding * 2}
                            fill="transparent"
                            stroke="#9CA3AF"
                            strokeWidth={scaleUIElement(1)}
                            dash={[4, 4]}
                            cornerRadius={scaleUIElement(2)}
                            opacity={0.6}
                        />
                    );
                }
                return null;
            })()}

            {/* Background image if available */}
            {backgroundImage &&
                backgroundImageUrl &&
                backgroundStatus === 'loaded' && (
                    <Image
                        x={8}
                        y={8}
                        width={SCREEN_WIDTH - 16}
                        height={SCREEN_HEIGHT - 16}
                        image={backgroundImage}
                    />
                )}

            {/* 9-Grid layout visualization */}
            {renderGridLayout()}

            {/* UI Elements preview */}
            {renderElements()}

            {/* Connection points */}
            {/* Top connection point */}
            <Circle
                x={SCREEN_WIDTH / 2}
                y={0}
                radius={scaleUIElement(4)}
                fill="#FFFFFF"
                stroke="#6B7280"
                strokeWidth={scaleUIElement(2)}
                visible={!isDragging}
            />

            {/* Bottom connection point */}
            <Circle
                x={SCREEN_WIDTH / 2}
                y={SCREEN_HEIGHT}
                radius={scaleUIElement(4)}
                fill="#FFFFFF"
                stroke="#6B7280"
                strokeWidth={scaleUIElement(2)}
                visible={!isDragging}
            />

            {/* Left connection point */}
            <Circle
                x={0}
                y={SCREEN_HEIGHT / 2}
                radius={scaleUIElement(4)}
                fill="#FFFFFF"
                stroke="#6B7280"
                strokeWidth={scaleUIElement(2)}
                visible={!isDragging}
            />

            {/* Right connection point */}
            <Circle
                x={SCREEN_WIDTH}
                y={SCREEN_HEIGHT / 2}
                radius={scaleUIElement(4)}
                fill="#FFFFFF"
                stroke="#6B7280"
                strokeWidth={scaleUIElement(2)}
                visible={!isDragging}
            />

            {/* Drop zone highlights */}
            {isDragOverScreen && renderDropZones}
        </Group>
    );
});

// Add context menu as a separate component that renders outside Konva
// eslint-disable-next-line
const ScreenCanvasNodeWithContextMenu = React.memo(
    function ScreenCanvasNodeWithContextMenu(props: ScreenCanvasNodeProps) {
        const [contextMenu, setContextMenu] = useState<{
            isOpen: boolean;
            position: { x: number; y: number };
            element: UIElement | null;
            screenId: string;
        }>({
            isOpen: false,
            position: { x: 0, y: 0 },
            element: null,
            screenId: '',
        });

        const dispatch = useDispatch();

        const handleContextMenu = useCallback(
            (element: UIElement, screenId: string, event: any) => {
                // Get the stage to convert coordinates to screen coordinates
                const stage = event.target.getStage();
                const stageContainer = stage.container();
                const rect = stageContainer.getBoundingClientRect();
                const pointerPosition = stage.getPointerPosition();

                setContextMenu({
                    isOpen: true,
                    position: {
                        x: rect.left + pointerPosition.x,
                        y: rect.top + pointerPosition.y,
                    },
                    element,
                    screenId,
                });
            },
            [],
        );

        const handleContextMenuClose = useCallback(() => {
            setContextMenu((prev) => ({ ...prev, isOpen: false }));
        }, []);

        const handleDeleteElement = useCallback(() => {
            if (contextMenu.element && contextMenu.screenId) {
                deleteElementWithUndo(
                    dispatch,
                    contextMenu.element.id,
                    contextMenu.screenId,
                    contextMenu.element,
                );
            }
        }, [dispatch, contextMenu]);

        const handleCopyElement = useCallback(() => {
            if (contextMenu.element) {
                elementClipboard.copy(contextMenu.element);
            }
        }, [contextMenu]);

        const handlePasteElement = useCallback(() => {
            const pastedElement = elementClipboard.paste();
            if (pastedElement && contextMenu.element && contextMenu.screenId) {
                // Paste in the same location as the context menu element
                copyElementWithUndo(
                    dispatch,
                    pastedElement,
                    contextMenu.screenId,
                    contextMenu.element.horizontalLocation,
                    contextMenu.element.verticalLocation,
                );
            }
        }, [dispatch, contextMenu]);

        // Create a wrapper function that matches the CanvasUIElementRenderer signature
        const wrappedContextMenuHandler = useCallback(
            (element: UIElement, screenId: string, event: any) => {
                handleContextMenu(element, screenId, event);
            },
            [handleContextMenu],
        );

        return (
            <>
                <ScreenCanvasNode
                    {...props}
                    onRightClick={wrappedContextMenuHandler}
                />
                <ElementContextMenu
                    isOpen={contextMenu.isOpen}
                    position={contextMenu.position}
                    onClose={handleContextMenuClose}
                    onDelete={handleDeleteElement}
                    onCopy={handleCopyElement}
                    onPaste={handlePasteElement}
                    canPaste={elementClipboard.canPaste()}
                />
            </>
        );
    },
);

export default ScreenCanvasNode;
